// Aperçu: the day's four numbers, the live map, what waits for a person,
// deliveries per day, Gazelle's briefing and the homes about to run out.
import { S } from '../lib/strings';
import { dayLabel, hhmm, minutesBetween } from '../lib/format';
import { statusPill } from '../lib/orders';
import type { Alert, Live, OpsOrder, Overview as Ov } from '../lib/types';
import { BarChart, DotMeter, Spark } from '../components/Charts';
import { Flame, Ic, type IconName } from '../components/Icon';
import { LiveMap } from '../components/LiveMap';
import { MiniBottle } from '../components/Bottles';
import type { Tab } from '../components/Shell';

interface Props { ov: Ov; live: Live | null; who: string; updatedAt: Date | null; go(t: Tab): void; openOrder(id: string): void; openGz(): void; openHome(id: string): void; remind?(ids: string[]): void }

function Kpi({ t, n, suf, foot, chart, icon, tone }: { t: string; n: number; suf?: string; foot: React.ReactNode; chart: React.ReactNode; icon: IconName; tone: '' | 'g' | 'r' | 'a' }) {
  return (
    <div className="card kpi">
      <div className="t"><span className={'ic ' + tone}><Ic n={icon} s="s" /></span>{t}</div>
      <div className="v"><span className="n num">{n}{suf && <small>{suf}</small>}</span>{chart}</div>
      <div className="f">{foot}</div>
    </div>
  );
}

function alertRow(a: Alert, openOrder: (id: string) => void, go: (t: Tab) => void, openHome: (id: string) => void) {
  switch (a.kind) {
    case 'unassigned': {
      const o = a.order;
      return { id: 'u' + o.id, c: 'violet', i: 'bolt' as IconName, t: S.alert.unassigned, s: `${o.customer.name} · ${o.customer.quartier}${o.marketer ? ' · ' + o.marketer.name : ''} · ${o.agency.name}`, a: S.alert.open, f: () => openOrder(o.id) };
    }
    case 'failed_install': {
      const o = a.order;
      return { id: 'f' + o.id, c: 'red', i: 'alert' as IconName, t: S.alert.failed, s: `${o.customer.name} · ${o.customer.quartier}${o.failure_note ? ' · ' + o.failure_note : ''}`, a: S.alert.open, f: () => openOrder(o.id) };
    }
    case 'silent_gateway': {
      const h = a.home, hrs = h.received_at ? Math.round(minutesBetween(h.received_at) / 60) : null;
      return { id: 's' + h.customer_id, c: 'red', i: 'wifi' as IconName, t: hrs != null ? S.alert.silent(hrs) : S.alert.silent(36), s: `${h.gateway} · ${h.name} · ${h.quartier}`, a: S.alert.see, f: () => openHome(h.customer_id) };
    }
    case 'low_without_order':
      return { id: 'low', c: 'blue', i: 'cyl' as IconName, t: S.alert.low(a.homes.length), s: S.alert.lowSub + a.homes.map((h) => `${h.name} (${h.level} %)`).join(', '), a: S.alert.see, f: () => (a.homes.length === 1 ? openHome(a.homes[0].customer_id) : go('hh')), ids: a.homes.map((h) => h.customer_id) };
  }
}

/** Gazelle's briefing, written from the same numbers until Gazelle itself answers (phase 4). */
function brief(ov: Ov): string[] {
  const out: string[] = [];
  const un = ov.alerts.filter((a) => a.kind === 'unassigned');
  out.push(un.length ? `${un.length === 1 ? (un[0] as { order: OpsOrder }).order.customer.name + ' attend' : un.length + ' commandes attendent'} un livreur. L’agence affecte depuis MyGaz Pro.` : 'Aucune commande n’attend de livreur.');
  const low = ov.alerts.find((a) => a.kind === 'low_without_order');
  out.push(low && low.kind === 'low_without_order' ? `${low.homes.length} foyer${low.homes.length > 1 ? 's passent' : ' passe'} sous 20 % sans avoir commandé : ${low.homes.map((h) => h.name).join(', ')}.` : 'Tous les foyers sous 20 % ont une commande en cours.');
  const silent = ov.homes.connected - ov.homes.online;
  out.push(silent ? `${silent} boîtier${silent > 1 ? 's' : ''} sur ${ov.homes.connected} ne répond${silent > 1 ? 'ent' : ''} plus depuis plus de 36 h.` : `Les ${ov.homes.connected} boîtiers répondent.`);
  return out;
}

export function Overview({ ov, live, who, updatedAt, go, openOrder, openGz, openHome, remind }: Props) {
  const d = new Date();
  const rows = ov.alerts.map((a) => alertRow(a, openOrder, go, openHome));
  const deliv = ov.days14.map((x) => x.delivered), ords = ov.orders14.map((x) => x.orders);
  const lastWeek = ords[6] ?? 0, diff = ov.today.orders - lastWeek;
  const silent = ov.homes.connected - ov.homes.online;
  return (
    <section className="view on" aria-label={S.tabs.ov}>
      <div className="ph">
        <div><h1>{S.hello(who.split(' ')[0])}</h1>
          <p>{d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Africa/Douala' })} · Yaoundé · <span className="num">{updatedAt ? S.updatedAt(hhmm(updatedAt)) : S.upToDate}</span></p>
        </div>
      </div>

      <div className="grid g4">
        <Kpi t={S.kpi.orders} n={ov.today.orders} icon="list" tone="" chart={<Spark vals={ords} />}
          foot={<><span className={'delta' + (diff < 0 ? ' dn' : diff === 0 ? ' flat' : '')}>{diff > 0 && <Ic n="up" s="xs" />}{diff > 0 ? '+' : ''}{diff}</span>{S.kpi.vsLast(dayLabel(new Date(Date.now() - 7 * 864e5), { weekday: 'long' }))}</>} />
        <Kpi t={S.kpi.delivered} n={ov.today.delivered} icon="check" tone="g" chart={<Spark vals={deliv} color="var(--green)" />}
          foot={<><span className="delta flat">{deliv.reduce((a, b) => a + b, 0)}</span>{S.kpi.over14}</>} />
        <Kpi t={S.kpi.low} n={ov.homes.low} suf={`/ ${ov.homes.connected}`} icon="cyl" tone="r"
          chart={<DotMeter on={ov.homes.connected - ov.homes.low} tot={ov.homes.connected} label={`${ov.homes.low} / ${ov.homes.connected}`} />}
          foot={ov.homes.low_no_order ? <><span className="delta dn">{S.kpi.withoutOrder(ov.homes.low_no_order)}</span>{S.kpi.served(ov.homes.low - ov.homes.low_no_order)}</> : <><span className="delta">{S.kpi.allServed}</span>{S.kpi.allServedSub}</>} />
        <Kpi t={S.kpi.online} n={ov.homes.online} suf={`/ ${ov.homes.connected}`} icon="wifi" tone=""
          chart={<DotMeter on={ov.homes.online} tot={ov.homes.connected} label={`${ov.homes.online} / ${ov.homes.connected}`} />}
          foot={silent ? <><span className="delta dn">{S.kpi.silent(silent)}</span>{S.kpi.over36}</> : <><span className="delta">{S.kpi.all}</span>{S.kpi.measures}</>} />
      </div>

      <div className="grid g-ov" style={{ marginTop: 16 }}>
        <div className="mapcard card" style={{ boxShadow: 'var(--shadow-1)' }}>
          <LiveMap live={live} padding={64} controls={false} onHome={openHome} onOrder={openOrder} />
          <div className="ov">
            <div className="glass livepill"><span className="livedot" />{S.live}<span className="muted" style={{ fontWeight: 500 }}>{S.riders(ov.riders.live)}</span></div>
            <div className="glass mstats">
              <div><b className="num">{ov.riders.en_route}</b><span>{S.enRoute}</span></div>
              <div><b className="num">{ov.riders.at_door}</b><span>{S.atDoor}</span></div>
              <div><b className="num">{Math.max(0, ov.riders.live - ov.riders.en_route - ov.riders.at_door)}</b><span>{S.free}</span></div>
            </div>
          </div>
          <div className="ov" style={{ top: 'auto', bottom: 14 }}><span /><button className="btn dark" onClick={() => go('live')}><Ic n="map" s="s" />{S.openMap}</button></div>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: 452 }} id="ov-todo">
          <div className="ch" style={{ paddingBottom: 12, borderBottom: '1px solid var(--line)' }}>
            <div><h2>{S.todo}</h2><div className="sub">{S.todoSub}</div></div>
            <span className="st grey nodot num">{S.topics(rows.length)}</span>
          </div>
          <div className="alerts" style={{ overflow: 'auto' }}>
            {rows.length ? rows.map((a) => (
              <div className="al" key={a.id}>
                <div className={'ai ' + a.c}><Ic n={a.i} s="s" /></div>
                <div style={{ minWidth: 0 }}><b>{a.t}</b><span>{a.s}</span></div>
                <div className="al-b">
                  {a.id === 'low' && remind && 'ids' in a && <button className="btn sm pri" onClick={() => remind(a.ids as string[])}>{S.act.remind}</button>}
                  <button className="btn sm" onClick={a.f}>{a.a}</button>
                </div>
              </div>
            )) : <div className="empty"><b>{S.nothing}</b>{S.nothingSub}</div>}
          </div>
        </div>
      </div>

      <div className="grid g-ov" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="ch"><div><h2>{S.perDay}</h2><div className="sub">{S.perDaySub}</div></div><button className="link" onClick={() => go('fin')}>{S.tabs.fin} <Ic n="arrow" s="xs" /></button></div>
          <div className="cb">
            <BarChart vals={deliv} labels={ov.days14.map((x, i) => (i === 13 ? S.todayShort : dayLabel(x.day + 'T12:00:00Z', { day: 'numeric' })))}
              o={{ cur: true, h: typeof innerWidth !== 'undefined' && innerWidth > 1100 ? 252 : 210, aria: S.perDaySub,
                tip: (i) => (<><b>{i === 13 ? S.today : dayLabel(ov.days14[i].day + 'T12:00:00Z', { weekday: 'long', day: 'numeric', month: 'short' })}</b><div className="tr"><span>{S.deliveries}</span>{deliv[i]}</div></>) }} />
          </div>
        </div>
        <div className="card pad brief">
          <div className="bh"><Flame /><b>{S.brief}</b><span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{updatedAt ? S.updatedAt(hhmm(updatedAt)) : ''}</span></div>
          <ol>{brief(ov).map((t, i) => (<li key={i}><i>{i + 1}</i><span>{t}</span></li>))}</ol>
          <button className="ask" onClick={() => openGz()}>{S.ask}<span className="go"><Ic n="arrow" s="s" /></span></button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="ch"><div><h2>{S.soon}</h2><div className="sub">{S.soonSub}</div></div><button className="link" onClick={() => go('hh')}>{S.allHomes} <Ic n="arrow" s="xs" /></button></div>
        <div className="cb">
          {ov.soon_empty.length ? (
            <div className="watch">
              {ov.soon_empty.map((h) => {
                const o = h.open_order;
                const pill = o ? (o.status === 'placed' ? <span className="st violet">{S.pill.toAssign}</span> : <span className="st blue">{statusPill(o.status)[0]}</span>)
                  : h.level < 20 ? <span className="st red">{S.pill.noOrder}</span> : <span className="st grey">{S.pill.notYet}</span>;
                return (
                  <button className="wc" key={h.customer_id} onClick={() => openHome(h.customer_id)}>
                    <MiniBottle level={h.level} w={30} anim />
                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                      <div className="nm">{h.name}</div>
                      <div className="q">{h.quartier}{h.marketer ? ' · ' + h.marketer.name : ''}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}><span className={'lv num' + (h.level < 20 ? ' lowtxt' : '')}>{Math.round(h.level)} %</span></div>
                      <div style={{ marginTop: 8 }}>{pill}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : <div className="empty"><b>{S.noHomes}</b>{S.noHomesSub}</div>}
        </div>
      </div>
    </section>
  );
}
